import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "wc-toast";
import { apiPost } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
import { isEmpty } from "lodash";
import { useContextSelector } from "use-context-selector";
import AuthContext from "../../context/AuthContext";
import iconBkash from "../../assets/images/bkash.png";
import iconRocket from "../../assets/images/rocket.png";
import iconNagad from "../../assets/images/nagad.png";
import { FaWallet } from "react-icons/fa";
import LinkWallet from "./LinkWallet";
import { useCallback } from "react";
import UnavailableFeatureDialog from "../../component/UnavailableFeatureDialog";

const FALLBACK_METHODS = [
  { key: "nagad", label: "NAGAD", icon: iconNagad },
  { key: "bkash", label: "BKASH", icon: iconBkash },
  { key: "rocket", label: "ROCKET", icon: iconRocket },
  { key: "send-bkash", label: "SEND MONEY BKASH", icon: iconBkash },
  { key: "send-nagad", label: "SEND MONEY NAGAD", icon: iconNagad },
];

export default function Withdraw({ setTab }) {
  const [linkWallet, setLinkWallet] = useState(false);
  const closeLinkWallet = useCallback(() => setLinkWallet(false), []);
  const [panel, setPanel] = useState("withdraw");
  const [bank, setBank] = useState([]);
  const [bankId, setBankId] = useState("");
  const [methodKey, setMethodKey] = useState("nagad");
  const [isLoader, setLoader] = useState(false);
  const [unavailable, setUnavailable] = useState("");
  const [minWithdraw, setMinWithdraw] = useState(100);
  const [maxWithdraw, setMaxWithdraw] = useState(49999);
  const profileData = useContextSelector(AuthContext, (ctx) => ctx.profileData);
  const userCoins = useContextSelector(AuthContext, (ctx) => ctx.userCoins);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true });

  const methods = useMemo(() => {
    return FALLBACK_METHODS.map((method) => {
      const matched = bank.find((item) => {
        const name = String(item?.bank_name || "").toLowerCase();
        if (method.key === "send-bkash") return name.includes("send") && name.includes("bkash");
        if (method.key === "send-nagad") return name.includes("send") && name.includes("nagad");
        return name.includes(method.key) && !name.includes("send");
      });
      return { ...method, id: matched?._id };
    });
  }, [bank]);

  const getBankDetail = async (id) => {
    if (!id) {
      setBankId("");
      setValue("accountName", "");
      setValue("bankAccount", "");
      return;
    }
    setBankId(id);
    const { status, data } = await apiPost(apiPath.getBankDetail, { bank: id });
    if (status === 200 && data?.success) {
      setValue("accountName", data?.results?.bankDetail?.account_name || "");
      setValue("bankAccount", data?.results?.bankDetail?.account_number || "");
    } else {
      setValue("accountName", "");
      setValue("bankAccount", "");
    }
  };

  const getBank = async () => {
    const { status, data } = await apiPost(apiPath.getBank, { type: "withdrawal" });
    if (status === 200 && data?.success) {
      const list = data?.results?.bankList || [];
      setBank(list);
      const nagad = list.find((item) => String(item?.bank_name || "").toLowerCase().includes("nagad") && !String(item?.bank_name || "").toLowerCase().includes("send"));
      if (nagad?._id) getBankDetail(nagad._id);
    }
  };

  useEffect(() => {
    getBank();
  }, []);

  useEffect(() => {
    if (!isEmpty(profileData) && profileData?.limitSetting?.length > 0) {
      setMinWithdraw(profileData.limitSetting[0]?.minWithdraw || 100);
      setMaxWithdraw(profileData.limitSetting[0]?.maxWithdraw || 49999);
    }
  }, [profileData]);

  const walletLinked = Boolean(watch("bankAccount"));
  const amount = Number(watch("amount") || 0);
  const balance = Number(userCoins?.balance || 0);
  const canSubmit = walletLinked && amount >= minWithdraw && amount <= maxWithdraw && amount <= balance;

  const onSubmit = async (body) => {
    if (!walletLinked) {
      setLinkWallet(true);
      return;
    }
    setLoader(true);
    const payload = {
      amount: body?.amount,
      BankAccount: body?.bankAccount,
      bank: bankId,
      AccountName: body?.accountName,
      phone_number: body?.bankAccount,
      pin: body?.verificationCode,
    };
    const { status, data } = await apiPost(apiPath.withdrawal, payload);
    if (status === 200 && data?.success) {
      toast.success(data?.message || "Withdrawal request submitted");
    } else {
      toast.error(data?.message || "Withdrawal failed");
    }
    setLoader(false);
  };

  const selectMethod = (method) => {
    setMethodKey(method.key);
    getBankDetail(method.id);
  };

  return (
    <div className="reference-withdraw" id="mc_container">
      <div className="reference-withdraw-tabs">
        <button type="button" className={panel === "withdraw" ? "active" : undefined} onClick={() => setPanel("withdraw")}>
          Withdraw
        </button>
        <button type="button" className={panel === "manage" ? "active" : undefined} onClick={() => setPanel("manage")}>
          Account Management
        </button>
        <button type="button" className="reference-withdraw-guide" onClick={() => setUnavailable("Withdrawal Guide")}>
          <i>i</i>
          Withdrawal Guide
        </button>
      </div>

      {panel === "manage" ? (
        <div className="reference-withdraw-manage">
          <h3>Account Management</h3>
          <p>Link or edit your e-wallets used for withdrawals.</p>
          <button type="button" className="reference-withdraw-add" onClick={() => setLinkWallet(true)}>
            + Add wallet
          </button>
        </div>
      ) : (
        <div className="reference-withdraw-body">
          <div className="reference-withdraw-main">
            <div className="reference-withdraw-methods" aria-label="Payment methods">
              {methods.map((method) => (
                <button
                  key={method.key}
                  type="button"
                  className={methodKey === method.key ? "active" : undefined}
                  aria-pressed={methodKey === method.key}
                  onClick={() => selectMethod(method)}
                >
                  <img src={method.icon} alt="" />
                  <span>{method.label.startsWith("SEND MONEY") ? <><small>Send Money</small><strong>{method.label.replace("SEND MONEY ", "")}</strong></> : <strong>{method.label}</strong>}</span>
                </button>
              ))}
              <button type="button" className="reference-withdraw-edit" aria-label="Edit wallets" onClick={() => setPanel("manage")}>
                <span aria-hidden="true">✎</span><strong>Edit wallets</strong>
              </button>
            </div>

            <form className="reference-withdraw-form" onSubmit={handleSubmit(onSubmit)}>
              {!walletLinked ? (
                <div className="reference-withdraw-empty">
                  <div className="reference-withdraw-card" aria-hidden="true" />
                  <p>No E-Wallet linked yet</p>
                  <button type="button" className="reference-withdraw-add" onClick={() => setLinkWallet(true)}>
                    + Add wallet
                  </button>
                </div>
              ) : (
                <div className="reference-withdraw-linked">
                  <div>
                    <span>Account Name</span>
                    <strong>{watch("accountName") || "—"}</strong>
                  </div>
                  <div>
                    <span>Wallet Number</span>
                    <strong>{watch("bankAccount")}</strong>
                  </div>
                </div>
              )}

              <aside className="reference-withdraw-time"><span>Withdrawal Time:</span><strong>24 hours</strong></aside>

              <div className="reference-withdraw-balances">
                <p>
                  <span>Central Wallet:</span> <strong>{balance.toFixed(2)}</strong>
                </p>
                <p>
                  <span>Available Amount:</span> <strong>{balance.toFixed(2)}</strong>
                </p>
              </div>

              <input type="hidden" {...register("accountName")} />
              <input type="hidden" {...register("bankAccount")} />
              <label className="reference-withdraw-amount"><span>Withdrawal</span>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder={`${minWithdraw} ~ ${maxWithdraw.toLocaleString("en-US")}`}
                  {...register("amount", {
                    required: "Please enter amount",
                    validate: (value) => {
                      const num = Number(value);
                      if (!(num > 0)) return "Amount should be greater than 0";
                      if (num < Number(minWithdraw)) return `Minimum amount is ${minWithdraw}`;
                      if (num > Number(maxWithdraw)) return `Maximum amount is ${maxWithdraw}`;
                      return true;
                    },
                  })}
                />
              </label>
              {errors?.amount?.message ? <em className="reference-withdraw-error">{errors.amount.message}</em> : null}

              <button type="submit" className="reference-withdraw-submit" disabled={!canSubmit || isLoader}>
                {isLoader ? "Submitting..." : "Submit"}
              </button>
              <p className="reference-withdraw-limit">Remaining Number of Withdrawal Today: <strong>9999</strong></p>
            </form>
          </div>

          <aside className="reference-withdraw-recent">
            <h3>Recent Withdrawal</h3>
            <div className="reference-withdraw-recent-empty">
              <span aria-hidden="true" />
              <p>No Withdrawal Request</p>
            </div>
            <button type="button" onClick={() => setTab?.("accountRecord")}>
              More
            </button>
          </aside>
        </div>
      )}

      {linkWallet && <button type="button" className="reference-link-wallet-shortcut open" aria-label="Link E-wallet" onClick={() => setLinkWallet(true)}><span>Link E-wallet</span><i aria-hidden="true"><FaWallet /></i></button>}
      {linkWallet && <LinkWallet initialMethod={methodKey} onClose={closeLinkWallet} onSaved={getBank} />}
      {unavailable && <UnavailableFeatureDialog feature={unavailable} onClose={() => setUnavailable("")} />}
    </div>
  );
}
